import React, { useEffect, useState } from 'react';
import { Input, Table, Space, Typography, Select, Segmented, Button, Card, Tag } from 'antd';
import { SearchOutlined, ApartmentOutlined, RightOutlined } from '@ant-design/icons';
import { get_formula_mappable_model_rows, get_formula_mappable_model_fields } from '@/utils/api';

const { Text } = Typography;

// Two lookup modes, both real options a tester picks between — not a
// forced escalation from one to the other:
//   Quick Search  — search/pick directly on this table (fast, when you
//                    already know something identifying about the row —
//                    a lot number, a code).
//   Guided Lookup — narrow down through related tables first (e.g. pick a
//                    Material, which narrows Material Inventory, which
//                    narrows Batch, which narrows Lot) — for when you don't
//                    know an identifying value on THIS table directly.
const MODE_QUICK = 'quick';
const MODE_GUIDED = 'guided';

// Friendly names only — never the raw "djangoapp.materialinventorybatch"
// label. The backend now sends related_model_verbose_name (real Django
// Meta.verbose_name) alongside every relation field, so there's no need to
// guess a display name client-side.
const friendlyTableName = (f) => f.related_model_verbose_name || f.related_model_label || f.name;
// Only used if a caller forgets to pass `label` — real verbose names should
// always come from the backend (see friendlyTableName / the `label` prop),
// not this best-effort guess.
const humanizeModelLabel = (modelLabel) =>
  (modelLabel || '').split('.').pop().replace(/_/g, ' ');

// Recursive "pick a real row, narrowing via related tables" picker.
//
// Given any model_label, this shows a search+table of its real rows (Quick
// Search), and — since Guided Lookup walks the model's own FK fields one
// hop at a time (get_formula_mappable_model_fields already supports this,
// dotted-path style) — a "narrow by related table" control that recurses
// into this same component for whichever related table is picked, letting
// the tester drill through related tables (e.g. Lot -> Batch -> Material
// Inventory -> Material) instead of typing a bare database id blind.
//
// Only ONE level is ever "live" on screen: while a deeper hop is still
// being decided, this level renders nothing but that child (no search/table
// of its own — it has nothing useful to show yet, unfiltered). The moment a
// row is picked at whatever level is currently live, it bubbles straight up
// through the onChange chain to whichever ancestor asked for it, which then
// re-renders itself as the live level — now filtered by that pick. So the
// picker always shows exactly one active breadcrumb trail + one active
// table, no matter how many hops deep the tester has gone.
//
// No table is special-cased: the same component works for any model_label,
// any depth, since it's built purely from field/row introspection.
export default function CascadingRecordPicker({
  modelLabel,
  label, // friendly name for THIS level — pass the real verbose name, never the raw model_label
  filters = {},
  value,
  onChange,
  depth = 0,
  maxDepth = 4,
  trail = [], // ancestor breadcrumb chips so far: [{ label, onBack }], oldest first
  initialMode = MODE_QUICK,
  hideModeSwitch = false,
  hideDirectInput = false,
  showCard = true,
  // Field names on THIS level's own model that the caller already knows
  // matter here (e.g. a Reference Value mapping's value_column) — shown
  // even if the generic column cap would otherwise cut them off. Only
  // meaningful at the level actually being resolved, so it isn't threaded
  // down into recursive drill-down children (those are different models).
  extraColumns = [],
}) {
  const [mode, setMode] = useState(initialMode);
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ columns: [], relation_columns: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [fkFields, setFkFields] = useState([]);
  const [drillField, setDrillField] = useState(null);
  const [drillValue, setDrillValue] = useState(null);
  const [drillLabel, setDrillLabel] = useState('');

  const isGuided = mode === MODE_GUIDED;
  const isDrilling = isGuided && !!drillField; // a related-table hop is in play
  const isDrillResolved = isDrilling && !!drillValue; // ...and it's been picked
  const isDrillPending = isDrilling && !drillValue; // ...still being decided below
  const effectiveFilters = isDrillResolved ? { ...filters, [`${drillField}_id`]: drillValue } : filters;
  const selfLabel = label || humanizeModelLabel(modelLabel);

  useEffect(() => {
    if (!modelLabel || isDrillPending) return undefined; // nothing useful to show yet
    let cancelled = false;
    setLoading(true);
    get_formula_mappable_model_rows(modelLabel, search, (res) => {
      if (cancelled) return;
      setData(res?.data?.data || { columns: [], relation_columns: [], rows: [] });
      setLoading(false);
    }, effectiveFilters, extraColumns);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelLabel, search, JSON.stringify(effectiveFilters), isDrillPending, JSON.stringify(extraColumns)]);

  useEffect(() => {
    if (!modelLabel || depth >= maxDepth) {
      setFkFields([]);
      return;
    }
    get_formula_mappable_model_fields(modelLabel, (res) => {
      setFkFields((res?.data?.data || []).filter((f) => f.is_relation && f.related_model_label));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelLabel, depth]);

  useEffect(() => {
    if (loading || isDrillPending) return;
    // In guided lookup, if the current narrowed step leaves exactly one row,
    // auto-pick it and bubble back up so the user doesn't have to click
    // through single-option tables one by one.
    if (isGuided && data.rows?.length === 1) {
      const onlyRow = data.rows[0];
      onChange(String(onlyRow.id), rowLabel(onlyRow), onlyRow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isGuided, isDrillPending, data.rows?.length]);

  if (!modelLabel) return null;

  const cancelDrill = () => {
    setDrillField(null);
    setDrillValue(null);
    setDrillLabel('');
  };

  // Still deciding a deeper hop — defer entirely to the child. It carries
  // this level's own label onto its breadcrumb trail, so nothing needs to
  // be rendered here (no duplicate "Pick a ..." header, no partial table).
  if (isDrillPending) {
    const chosenFkField = fkFields.find((f) => f.name === drillField);
    if (chosenFkField) {
      const child = (
        <CascadingRecordPicker
          modelLabel={chosenFkField.related_model_label}
          label={friendlyTableName(chosenFkField)}
          value={drillValue}
          onChange={(id, lbl) => {
            setDrillValue(id);
            setDrillLabel(lbl);
          }}
          depth={depth + 1}
          maxDepth={maxDepth}
          trail={[...trail, { label: selfLabel, onBack: cancelDrill }]}
          initialMode={MODE_GUIDED}
          hideModeSwitch
          hideDirectInput
          showCard={false}
        />
      );
      return showCard ? (
        <Card size="small" styles={{ body: { padding: depth === 0 ? 16 : 12 } }}>
          {child}
        </Card>
      ) : (
        child
      );
    }
  }

  const chosenFkField = fkFields.find((f) => f.name === drillField);
  const rowLabel = (record) =>
    data.columns
      .map((c) => (typeof record[c] === 'string' ? record[c] : ''))
      .filter(Boolean)
      .slice(0, 2)
      .join(' · ') || `#${record.id}`;

  const panelStyle = {
    borderRadius: 14,
    border: '1px solid #dbe5f0',
    background: depth === 0 ? 'linear-gradient(180deg, #fbfdff 0%, #f5f9ff 100%)' : '#ffffff',
    boxShadow: depth === 0 ? '0 10px 24px rgba(15, 23, 42, 0.06)' : 'none',
  };

  const content = (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {!hideModeSwitch && fkFields.length > 0 && (
        <Segmented
          block
          size="middle"
          value={mode}
          onChange={(v) => {
            setMode(v);
            if (v === MODE_QUICK) cancelDrill();
          }}
          options={[
            {
              label: (
                <Space size={6}>
                  <SearchOutlined />
                  <span>Quick</span>
                </Space>
              ),
              value: MODE_QUICK,
            },
            {
              label: (
                <Space size={6}>
                  <ApartmentOutlined />
                  <span>Guided</span>
                </Space>
              ),
              value: MODE_GUIDED,
            },
          ]}
        />
      )}

      {trail.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '2px 0' }}>
          {trail.map((step, index) => (
            <React.Fragment key={`${step.label}-${index}`}>
              <Button
                type="link"
                size="small"
                onClick={step.onBack}
                style={{
                  height: 30,
                  padding: '0 14px',
                  borderRadius: 999,
                  border: '1px solid #d9e3f0',
                  background: '#f8fbff',
                  color: '#5f7190',
                  fontWeight: 500,
                }}
              >
                {step.label}
              </Button>
              <RightOutlined style={{ fontSize: 11, color: '#94a3b8' }} />
            </React.Fragment>
          ))}
          <Tag
            color="blue"
            style={{
              marginInlineEnd: 0,
              borderRadius: 999,
              paddingInline: 14,
              paddingBlock: 4,
              fontWeight: 600,
              background: '#eff6ff',
              borderColor: '#93c5fd',
              color: '#2563eb',
            }}
          >
            {selfLabel}
          </Tag>
        </div>
      )}

      {isGuided && !isDrilling && fkFields.length > 0 && (
        <div style={{ border: '1px dashed #cdd9e8', borderRadius: 12, padding: 12, background: '#ffffff' }}>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
            Don&apos;t know a {selfLabel} value directly? Narrow it down via a related table instead:
          </Text>
          <Select
            size="middle"
            style={{ width: '100%' }}
            allowClear
            placeholder="Choose a related table to narrow by"
            value={undefined}
            options={fkFields.map((f) => ({ value: f.name, label: friendlyTableName(f) }))}
            onChange={(v) => setDrillField(v || null)}
          />
        </div>
      )}

      {isDrillResolved && chosenFkField && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            borderRadius: 12,
            padding: '10px 12px',
            background: '#eef6ff',
            border: '1px solid #c7ddff',
          }}
        >
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              Narrowed by
            </Text>
            <Space size={8} wrap>
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {friendlyTableName(chosenFkField)}
              </Tag>
              <Text strong style={{ fontSize: 12 }}>
                {drillLabel}
              </Text>
            </Space>
          </div>
          <Button size="small" onClick={cancelDrill}>
            Change
          </Button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: depth === 0 && !hideDirectInput ? 'minmax(0, 1fr) 180px' : 'minmax(0, 1fr)',
          gap: 12,
          alignItems: 'end',
        }}
      >
        <div>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
            Search {selfLabel}
          </Text>
          <Input size="middle" placeholder="Search records..." value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
        </div>
        {depth === 0 && !hideDirectInput && (
          <div>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
              Enter ID directly
            </Text>
            <Input size="middle" placeholder="e.g. 6" value={value || ''} onChange={(e) => onChange(e.target.value, e.target.value)} />
          </div>
        )}
      </div>

      <Table
        size="small"
        bordered
        loading={loading}
        dataSource={data.rows}
        rowKey="id"
        pagination={{ pageSize: 5, size: 'small' }}
        scroll={{ x: 'max-content' }}
        onRow={(record) => ({
          onClick: () => onChange(String(record.id), rowLabel(record), record),
          style: { cursor: 'pointer', background: String(record.id) === String(value) ? '#e6f4ff' : undefined },
        })}
        columns={[
          { dataIndex: 'id', title: 'ID', width: 72, fixed: 'left' },
          ...data.columns.map((c) => ({ dataIndex: c, title: c, width: 180, ellipsis: true })),
        ]}
      />
    </Space>
  );

  return showCard ? (
    <Card size="small" styles={{ body: { padding: depth === 0 ? 16 : 12 } }} style={panelStyle}>
      {content}
    </Card>
  ) : (
    content
  );
}
